/**
 * useAwareness - React hook for presence and cursor state
 *
 * Provides:
 * - Current user's awareness state (cursor, selection)
 * - All remote users' awareness states
 * - Methods to update local awareness
 *
 * @param awareness - The Awareness instance from useYjsProvider
 * @param localUser - Current user info (id, name, color)
 */

import { useState, useEffect, useCallback } from 'react';
import * as awarenessProtocol from 'y-protocols/awareness';
import type { AwarenessState, PresenceUser, CursorPosition } from '@deep-agents/shared-types';

const DEBUG_COLLAB = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('DEBUG_COLLAB') === '1';
  } catch {
    return false;
  }
})();

const debugLog = (...args: any[]) => {
  if (DEBUG_COLLAB) {
    console.log(...args);
  }
};

interface UseAwarenessReturn {
  // Map of clientId -> awareness state
  states: Map<number, AwarenessState>;
  // Update local canvas cursor position
  setCursor: (cursor: CursorPosition | null) => void;
  // Update local selection (for Phase 4/5)
  setSelection: (selection: AwarenessState['selection']) => void;
  // Update local viewport (for follow mode)
  setViewport: (viewport: AwarenessState['viewport']) => void;
  // Get list of online users (excluding self)
  onlineUsers: PresenceUser[];
}

export function useAwareness(
  awareness: awarenessProtocol.Awareness,
  localUser: PresenceUser
): UseAwarenessReturn {
  const [states, setStates] = useState<Map<number, AwarenessState>>(new Map());

  // Set initial local awareness state
  useEffect(() => {
    awareness.setLocalStateField('user', localUser);
    awareness.setLocalStateField('canvasCursor', null);
    awareness.setLocalStateField('selection', null);
  }, [awareness, localUser]);

  // Subscribe to awareness changes
  useEffect(() => {
    const updateStates = () => {
      const newStates = new Map<number, AwarenessState>();
      awareness.getStates().forEach((state, clientId) => {
        if (state.user) {
          newStates.set(clientId, state as AwarenessState);
        }
      });
      setStates(newStates);
      debugLog('[useAwareness] awareness states updated:', newStates.size);
    };

    // Initial state
    updateStates();

    // Subscribe to changes
    awareness.on('change', updateStates);

    return () => {
      awareness.off('change', updateStates);
    };
  }, [awareness]);

  // Update canvas cursor position
  const setCursor = useCallback((cursor: CursorPosition | null) => {
    awareness.setLocalStateField('canvasCursor', cursor);
  }, [awareness]);

  // Update selection (Phase 4/5 will use this)
  const setSelection = useCallback((selection: AwarenessState['selection']) => {
    awareness.setLocalStateField('selection', selection);
  }, [awareness]);

  // Update viewport (for follow mode)
  const setViewport = useCallback((viewport: AwarenessState['viewport']) => {
    awareness.setLocalStateField('viewport', viewport);
  }, [awareness]);

  // Get online users (excluding self), de-duplicated by user ID
  const onlineUsersById = new Map<string, PresenceUser>();
  const localClientId = awareness.clientID;
  states.forEach((state, clientId) => {
    if (clientId !== localClientId && state.user) {
      const userId = state.user.id || String(clientId);
      if (!onlineUsersById.has(userId)) {
        onlineUsersById.set(userId, state.user);
      }
    }
  });
  const onlineUsers = Array.from(onlineUsersById.values());

  return {
    states,
    setCursor,
    setSelection,
    setViewport,
    onlineUsers,
  };
}
